import React, { useEffect, useState } from 'react';
import Joyride, { ACTIONS, EVENTS } from 'react-joyride';
import { useDispatch, useSelector } from 'react-redux';

import { ANNOUNCEMENTS } from 'components/FeatureAnnouncement/announcements';
import { NEEDS_SANDBOX_ADMIN } from 'layouts/V2/Dashboard/components/SwitchEnvironment/constants';
import PropTypes from 'prop-types';

import { selectIsSandboxAdminExist } from 'redux/selectors/auth';
import { selectAnnouncementCompanyKey, selectSeenAnnouncements } from 'redux/selectors/general';
import { markAnnouncementSeen } from 'redux/slices/general';
import { dismissSidebarTour } from 'redux/slices/sidebarTour';

import TourTooltip from './components/TourTooltip';
import { JOYRIDE_STYLES, TOUR_ID, TOUR_LOCALE, getTourSteps } from './constants';

// Walks each company through the new sidebar once, after any pending feature announcement.
function SidebarTour({ isSmallScreen }) {
    const dispatch = useDispatch();
    const companyKey = useSelector(selectAnnouncementCompanyKey);
    const seenAnnouncements = useSelector(selectSeenAnnouncements);
    const isCollapsed = useSelector((state) => state.general?.sidebarCollapse);
    const isSandboxAdminExist = useSelector(selectIsSandboxAdminExist);
    const isDismissed = useSelector((state) => state.sidebarTour?.isDismissed);
    const [steps, setSteps] = useState([]);

    const hasPendingAnnouncement = ANNOUNCEMENTS.some(({ id }) => !seenAnnouncements.includes(id));
    const shouldRun =
        Boolean(companyKey) &&
        !hasPendingAnnouncement &&
        !isDismissed &&
        !seenAnnouncements.includes(TOUR_ID);

    // Skip steps whose target this admin can't see, so the step count stays honest.
    // Each tooltip opens straight away instead of waiting behind a beacon dot.
    useEffect(() => {
        if (!shouldRun) {
            setSteps([]);
            return;
        }
        const canSwitchEnvironment = !NEEDS_SANDBOX_ADMIN || isSandboxAdminExist;
        setSteps(
            getTourSteps({ isSmallScreen, isCollapsed, canSwitchEnvironment })
                .filter((step) => document.querySelector(step.target))
                .map((step) => ({ ...step, disableBeacon: true }))
        );
        // Built once per run; later changes would restart the tour mid-way.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldRun]);

    // Finishing or skipping ends the tour for good. Esc only hides it until the next visit,
    // since it is easy to press by accident.
    const handleCallback = ({ action, type }) => {
        if (type === EVENTS.TOUR_END) {
            dispatch(markAnnouncementSeen({ companyKey, id: TOUR_ID }));
        } else if (action === ACTIONS.CLOSE && type === EVENTS.STEP_AFTER) {
            dispatch(dismissSidebarTour());
        }
    };

    if (!shouldRun || steps.length === 0) {
        return null;
    }

    return (
        <Joyride
            run
            continuous
            steps={steps}
            callback={handleCallback}
            tooltipComponent={TourTooltip}
            locale={TOUR_LOCALE}
            styles={JOYRIDE_STYLES}
            spotlightPadding={4}
            disableOverlayClose
            // Every target is always on screen, and scrolling shifted the whole app.
            disableScrolling
        />
    );
}

SidebarTour.propTypes = {
    isSmallScreen: PropTypes.bool,
};

export default SidebarTour;
